// Layered ordering of ongoing effects. Pure. No React, no Three.js.
//
// ── Codex (Ordering Ongoing Effects) ──
//
// Sorcery uses a layer system to resolve interactions between
// continuous effects. The codex is upfront: "do what's intuitive and
// you'll get the right result." But when multiple effects compete on
// the same property, the layer + timestamp rules disambiguate.
//
// The full algorithm:
//   1) Group effects by LAYER.
//   2) Within a layer (or across layers, when stacking), sort by
//      TIMESTAMP, oldest first.
//   3) Re-arrange certain conditional effects so they resolve before
//      the layer of the ability they add/remove (e.g. Land Deed adds
//      a control ability → applies just before layer 4, not in 5).
//   4) Dependency rule for control effects (Mesmerism vs King). The
//      independent effect applies first.
//   5) "At rest" tiebreak for Hillock Basilisk / Stone-gaze Gorgons.
//
// ── This module (v1.58 MVP) ──
//
// Provides the layer enum, an effect classifier, and the sort. It
// implements steps 1 and 2 (group + timestamp). Which together cover
// the well-known canonical examples (Two Kings of the Realm, Two
// Smokestacks of Gnaak, Flood vs Drought) once the caller applies
// effects in the returned order AND each effect reads the CURRENT
// in-progress state (so the later King sees the earlier King's claim).
//
// Steps 3-5 are intentionally deferred:
//   • Conditional re-arrange (Bound Spirit + Eerie Coral, Pied Piper
//     of Hameln, Plate of the Whale). Needs the runtime to flag the
//     specific cards/effects that opt into the re-arrange.
//   • Dependency rule (Mesmerism vs King). Needs explicit dependency
//     detection.
//   • At-rest tiebreak. Only matters when two Hillock Basilisks /
//     Stone-gaze Gorgons attempt to disable each other.
//
// Each deferred case is flagged in the manifest. When a card hits one
// of these patterns, extend this module rather than adding a special
// case in GameBoard.

import { OP } from '../automation/schema';

/**
 * Layer numbers from the Sorcery codex. Lower number = applies first.
 * Names match the codex verbatim so adding new effects feels obvious.
 */
export const LAYER = Object.freeze({
  COPY:           1, // Mirror Realm, Selfsame Simulacrum
  TYPE:           2, // Bower of Bliss, Corruptor, Enchantress
  ABILITY_REMOVE: 3, // Root Spider, Sisters of Silence, Smokestacks
  CONTROL:        4, // King of the Realm, Mesmerism
  ABILITY_ADD:    5, // Power of Flight, Watchtower, Tide Naiads, Drought
  POWER_MOD:      6, // Shrink (set), Overpower (+/-), Warp Spasm (×)
});

/**
 * Power-modification sub-order. Within layer 6, codex orders:
 *   • set effects first (Shrink)
 *   • then add/subtract (Overpower)
 *   • then multiply (Warp Spasm)
 * Returned as a sub-layer integer so sort stays stable across all
 * layers. None of the v1.58 ops are multiplicative or setting; every
 * GRANT_STAT today is additive, so they all get POWER_MOD_ADD.
 */
export const POWER_SUB = Object.freeze({
  SET:      0,
  ADD_SUB:  1,
  MULTIPLY: 2,
});

/**
 * Classify a single effect by op + args to one of the LAYER values.
 * Returns POWER_MOD as the conservative default for unknown ops so
 * callers don't blow up on schema additions. They just get applied
 * last in the pipeline. Manifest review catches the mis-class on the
 * next iteration.
 *
 * Conditional re-arrange (per codex: "an effect that adds or removes
 * an ability does so before that ability's layer") is NOT applied
 * here. See adjustLayerForCondition for the future hook.
 */
export function classifyEffect(op, _args) {
  switch (op) {
    // Layer 5. Ability + affinity grants. Threshold/Mana grants
    // change a player's affinity profile (codex: "affinity-changing
    // effects" sit in layer 5).
    case OP.GRANT_KEYWORD:
    case OP.GRANT_THRESHOLD:
    case OP.GRANT_MANA:
      return LAYER.ABILITY_ADD;

    // Layer 2. Type-changing. Corruptor's subtype replacement, and
    // name aliases ("Counts as a Grey Wolves"). Both must exist
    // before layer-6 per-name power counting reads them.
    case OP.TRANSFORM_SUBTYPES:
    case OP.ADD_NAME_ALIAS:
      return LAYER.TYPE;

    // Layer 4. Control. Returned King / King of the Realm pattern.
    case OP.GRANT_CONTROL:
      return LAYER.CONTROL;

    // Layer 6. Power modification. Sorcery's "+N power" hits both
    // halves (ATK + HP), but the layer assignment is still 6. The
    // additive sub-order kicks in within the layer.
    case OP.GRANT_STAT:
    case OP.GRANT_STAT_PER_EACH_SELF:
      return LAYER.POWER_MOD;

    default:
      // Schema additions (DISABLE / SILENCE / TYPE_CHANGE / COPY)
      // should explicitly land here once parser/runtime support them.
      // Until then, conservative POWER_MOD keeps them last.
      return LAYER.POWER_MOD;
  }
}

/**
 * Sub-layer integer within POWER_MOD. All current GRANT_STAT ops are
 * additive (delta:+N or -N), so they all map to ADD_SUB. Reserved for
 * the day Shrink (SET) and Warp Spasm (MULTIPLY) land.
 */
export function classifyPowerSub(op, _args) {
  if (op !== OP.GRANT_STAT && op !== OP.GRANT_STAT_PER_EACH_SELF) return POWER_SUB.ADD_SUB;
  return POWER_SUB.ADD_SUB;
}

/**
 * Sort a list of effect descriptors into the layered order:
 *   1) layer asc
 *   2) powerSub asc (only meaningful inside layer 6)
 *   3) timestamp asc. Older effects apply first so the newer ones
 *      see the older effect's contribution and can build on top
 *      (or, for control, see who NOW controls them)
 *   4) active-player tiebreak. When two sources entered simultaneously
 *      (rare: Boneyard mass-summon), the active player's source is
 *      treated as earlier per codex
 *   5) sourceCardId asc as the final stable tiebreaker
 *
 * Returns a new array; does NOT mutate `effects`.
 *
 * Each effect descriptor MUST carry:
 *   { layer, powerSub, timestamp, activePlayerTie, sourceCardId, ...rest }
 *
 * `activePlayerTie` is a boolean. True iff the source's controller
 * is the active player on the simultaneous-entry tie boundary.
 */
export function sortLayered(effects) {
  return [...(effects || [])].sort(compareLayered);
}

function compareLayered(a, b) {
  if (a.layer !== b.layer) return a.layer - b.layer;
  if ((a.powerSub || 0) !== (b.powerSub || 0)) return (a.powerSub || 0) - (b.powerSub || 0);
  if (a.timestamp !== b.timestamp) return a.timestamp - b.timestamp;
  if (a.activePlayerTie !== b.activePlayerTie) return a.activePlayerTie ? -1 : 1;
  // Stable tiebreaker. String compare on the source id keeps the
  // sort deterministic across runs (critical for the per-recompute
  // ordering to be the same on both clients in P2P).
  return String(a.sourceCardId || '').localeCompare(String(b.sourceCardId || ''));
}

/**
 * Monotonic timestamp generator. Each call returns a strictly larger
 * integer than the previous, so a single shared instance can stamp
 * card entries and triggered/activated ability outputs in the order
 * they happen. Lives outside the engine; GameBoard owns one and
 * passes results into addCardToTable / ability-trigger paths.
 */
export function makeTimestampGen() {
  let next = 0;
  return () => {
    const t = next;
    next += 1;
    return t;
  };
}

/**
 * Future hook for codex step 3 ("conditional re-arrange"). Returns
 * the effect's effective layer after considering whether it adds or
 * removes an ability. The codex example: Land Deed adds a control
 * ability to its bearer, so although Land Deed is itself an
 * ability-grant (layer 5), the effect resolves just before layer 4.
 *
 * Today this is a passthrough. Extend when the parser learns to
 * tag affected ops with `_addsAbilityForLayer` / `_removesAbilityForLayer`.
 */
export function adjustLayerForCondition(layer, _effectMeta) {
  return layer;
}
